import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./Login.css";
import { login, loginWithGoogle, loginWithStage5Token, signOutCurrentUser } from "../../firebase";
import { callStage5DirectFree, STAGE5_DIRECT_FREE_ENABLED } from "../../Utils/stage5DirectFreeApi";
import { callStage5PurchaseClaim, STAGE5_PAID_CLAIM_ENABLED } from "../../Utils/stage5PurchaseClaimApi";
import { exchangeAdminEventAccess } from "../../Utils/adminEventAccessApi";

const SIGNUP_FIELDS = [
  ["firstName", "שם פרטי"],
  ["lastName", "שם משפחה"],
  ["email", "אימייל"],
  ["phone", "טלפון"],
];

const Login = ({ addAlertText, setDocID, initialSignupMode = false }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleProcessing, setIsGoogleProcessing] = useState(false);
  const [isEmailProcessing, setIsEmailProcessing] = useState(false);
  const [signupMode, setSignupMode] = useState(Boolean(initialSignupMode && STAGE5_DIRECT_FREE_ENABLED));
  const [signup, setSignup] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [signupErrors, setSignupErrors] = useState({});
  const [signupState, setSignupState] = useState("idle");
  const [purchaseClaimToken, setPurchaseClaimToken] = useState("");
  const [purchaseClaimState, setPurchaseClaimState] = useState("idle");
  const [eventAccessState, setEventAccessState] = useState("idle");
  const [adminAccess, setAdminAccess] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const completeLogin = useCallback((res) => {
    setDocID(res.user.uid);
    window.sessionStorage.setItem("login", JSON.stringify({ id: res.user.uid, isAuth: true }));
    addAlertText({ text: "התחברתם בהצלחה!", mode: "success" });
    if (res.user.email === "admin@gmail.com") {
      window.location.href = "/admin";
      return;
    }
    // Firebase already holds the new session in this page, and the router re-checks the
    // event on sign-in, so moving in-app avoids downloading and starting the app twice.
    navigate("/manage", { replace: true });
  }, [addAlertText, navigate, setDocID]);

  const showLoginError = () => {
    addAlertText({ text: "פרטי ההתחברות אינם נכונים", mode: "warning" });
  };

  // Admin EVENTS dashboard: a one-time link (valid 2 minutes) that signs in to one event.
  useEffect(() => {
    const prefix = "#admin-event-access=";
    if (!window.location.hash.startsWith(prefix)) return;
    const token = decodeURIComponent(window.location.hash.slice(prefix.length));
    window.history.replaceState({}, "", `${window.location.pathname}${window.location.search}`);
    setAdminAccess(true);
    setEventAccessState("signing-in");
    exchangeAdminEventAccess(token)
      .then(async (result) => completeLogin(await loginWithStage5Token(result.customToken)))
      .catch((error) => {
        const permanentFailure = ["ADMIN_ACCESS_INVALID", "ADMIN_ACCESS_USED", "ADMIN_ACCESS_EXPIRED"].includes(error.code);
        setEventAccessState(permanentFailure ? "invalid" : "temporary-error");
      });
  }, [completeLogin]);

  useEffect(() => {
    if (!STAGE5_DIRECT_FREE_ENABLED) return;
    const prefix = "#stage5-email-claim=";
    if (!window.location.hash.startsWith(prefix)) return;
    setSignupMode(true);
    const token = decodeURIComponent(window.location.hash.slice(prefix.length));
    window.history.replaceState({}, "", `${window.location.pathname}${window.location.search}`);
    setSignupState("creating");
    callStage5DirectFree({ action: "consumeEmailSignupClaim", payload: { token } })
      .then(async (result) => {
        if (result.status === "EXISTING_EVENT") {
          if (!result.customToken) throw Object.assign(new Error("STAGE5_EXISTING_EVENT_AUTH_REQUIRED"), { code: "STAGE5_EXISTING_EVENT_AUTH_REQUIRED" });
          await loginWithStage5Token(result.customToken);
          setSignupState("existing-event");
          return;
        }
        if (!result.customToken) throw Object.assign(new Error("STAGE5_EMAIL_CLAIM_USED"), { code: result.status });
        const signedIn = await loginWithStage5Token(result.customToken);
        completeLogin(signedIn);
      })
      .catch((error) => {
        setSignupState(error.code === "EXPIRED" ? "expired" : "used");
      });
  }, [completeLogin]);

  useEffect(() => {
    if (!STAGE5_PAID_CLAIM_ENABLED) return;
    const purchasePrefix = "#stage5-purchase-claim=";
    const accessPrefix = "#stage5-event-access=";
    if (window.location.hash.startsWith(purchasePrefix)) {
      const token = decodeURIComponent(window.location.hash.slice(purchasePrefix.length));
      setPurchaseClaimToken(token);
      setPurchaseClaimState("creating");
      window.history.replaceState({}, "", `${window.location.pathname}${window.location.search}`);
      callStage5PurchaseClaim({ action: "consumePurchaseClaim", token })
        .then(async (result) => {
          if ((result.status === "CREATED" || result.status === "ALREADY_CREATED") && result.customToken) {
            setPurchaseClaimState("signing-in");
            try {
              completeLogin(await loginWithStage5Token(result.customToken));
            } catch (_) {
              setPurchaseClaimState("access-failed");
            }
            return;
          }
          setPurchaseClaimState(result.status.toLowerCase());
        })
        .catch((error) => setPurchaseClaimState(error.code === "STAGE5_RESEND_COOLDOWN" ? "cooldown" : "error"));
      return;
    }
    if (window.location.hash.startsWith(accessPrefix)) {
      const token = decodeURIComponent(window.location.hash.slice(accessPrefix.length));
      setEventAccessState("signing-in");
      window.history.replaceState({}, "", `${window.location.pathname}${window.location.search}`);
      callStage5PurchaseClaim({ action: "exchangeEventAccess", token })
        .then(async (result) => completeLogin(await loginWithStage5Token(result.customToken)))
        .catch((error) => {
          const permanentFailure = ["STAGE5_ACCESS_INVALID", "STAGE5_ACCESS_REVOKED", "STAGE5_ACCESS_EVENT_INVALID"].includes(error.code);
          setEventAccessState(permanentFailure ? "invalid" : "temporary-error");
        });
    }
  }, [completeLogin]);

  useEffect(() => {
    const urlUsername = searchParams.get("username");
    const urlPassword = searchParams.get("password");

    if (!urlUsername || !urlPassword) return;

    const autoLogin = async () => {
      const res = await login({ password: urlPassword, username: urlUsername });
      if (res) {
        setDocID(res.user.uid);
        window.sessionStorage.setItem("login", JSON.stringify({ id: res.user.uid, isAuth: true }));
        addAlertText({ text: "התחברתם בהצלחה!", mode: "success" });
        window.location.href = res.user.email === "admin@gmail.com" ? "/admin" : "/manage";
        return;
      }
      addAlertText({ text: "פרטי ההתחברות אינם נכונים", mode: "warning" });
    };

    autoLogin();
  }, [addAlertText, searchParams, setDocID]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    const res = await login({ password, username });
    setIsSubmitting(false);

    if (res) {
      completeLogin(res);
      return;
    }
    showLoginError();
  };

  const handleGoogleSignup = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const signedIn = await loginWithGoogle();
      setIsGoogleProcessing(true);
      const idToken = await signedIn.user.getIdToken();
      const result = await callStage5DirectFree({ action: "createDirectFreeEvent", idToken, payload: { profile: {} } });
      if (result.status === "EXISTING_EVENT") {
        setSignupState("existing-event");
        return;
      }
      completeLogin(signedIn);
    } catch (error) {
      await signOutCurrentUser().catch(() => undefined);
      if (error.code === "STAGE5_DARK_ALLOWLIST_REQUIRED") setSignupState("unavailable");
      else addAlertText({ text: "לא הצלחנו לפתוח מערכת חדשה. נסו שוב.", mode: "warning" });
    } finally {
      setIsGoogleProcessing(false);
      setIsSubmitting(false);
    }
  };

  const handleEmailSignup = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    const requiredFieldErrors = Object.fromEntries(
      SIGNUP_FIELDS
        .filter(([key]) => !signup[key].trim())
        .map(([key, label]) => [key, `יש למלא ${label}`])
    );
    if (Object.keys(requiredFieldErrors).length > 0) {
      setSignupErrors(requiredFieldErrors);
      return;
    }

    setSignupErrors({});
    setIsEmailProcessing(true);
    setIsSubmitting(true);
    try {
      await callStage5DirectFree({ action: "startEmailSignupClaim", payload: signup });
      setSignupState("check-email");
    } catch (error) {
      if (error.code === "STAGE5_DARK_ALLOWLIST_REQUIRED") setSignupState("unavailable");
      else addAlertText({ text: "לא הצלחנו לשלוח את קישור ההרשמה. נסו שוב.", mode: "warning" });
    } finally {
      setIsEmailProcessing(false);
      setIsSubmitting(false);
    }
  };

  const resendPurchaseAccess = async () => {
    if (!purchaseClaimToken || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = await callStage5PurchaseClaim({ action: "resendAccessEmail", token: purchaseClaimToken });
      setPurchaseClaimState(result.status === "EMAIL_ACCEPTED" ? "resent" : "email-failed");
    } catch (error) {
      setPurchaseClaimState(error.code === "STAGE5_RESEND_COOLDOWN" ? "cooldown" : "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="login-page" dir="rtl">
      <div className="login-page__glow login-page__glow--top" aria-hidden="true" />
      <div className="login-page__glow login-page__glow--bottom" aria-hidden="true" />

      <section className="login-card" aria-labelledby="login-title">
        {purchaseClaimState === "created" ? (
          <div className="login-confetti" aria-hidden="true">
            {Array.from({ length: 12 }, (_, index) => <span className="login-confetti__piece" key={index} />)}
          </div>
        ) : null}
        <header className="login-card__header">
          <div className="login-card__logo-wrap">
            <img src="/assets/icons/ronenlogo.png" alt="פלאשבק הפקות" className="login-card__logo" />
          </div>
          <span className="login-card__eyebrow">פלאשבק הפקות</span>
          <h1 id="login-title">{eventAccessState !== "idle" ? (adminAccess ? "כניסת מנהל לאירוע" : "כניסה מאובטחת למערכת") : purchaseClaimToken ? <>מזל טוב!<br />המערכת נפתחה</> : signupMode ? "הרשמה ופתיחת מערכת אישורי הגעה" : "כניסה למערכת"}</h1>
          <p>{eventAccessState !== "idle" ? (adminAccess ? "קישור מנהל חד־פעמי מלוח האירועים." : "זהו קישור הגישה הקבוע למערכת שלכם, והוא יעבוד גם בעתיד.") : purchaseClaimToken ? "תוכלו לשלוח אישורי הגעה" : signupMode ? "המערכת החינמית מוגבלת ל-50 רשומות/אישורי הגעה." : "ניהול אישורי הגעה לאירוע שלכם, במקום אחד."}</p>
        </header>

        {eventAccessState !== "idle" ? (
          <div className="login-signup-status" role="status">
            {eventAccessState === "signing-in" ? <span className="login-signin-status"><span className="login-progress-spinner login-progress-spinner--small" aria-hidden="true" /><span>נכנסים למערכת...</span></span> : adminAccess ? (eventAccessState === "invalid" ? "קישור המנהל פג תוקף או כבר נוצל. פתחו קישור חדש מלוח האירועים." : "לא הצלחנו להתחבר כרגע. פתחו קישור חדש מלוח האירועים.") : eventAccessState === "invalid" ? "קישור הגישה אינו תקין או בוטל. בקשו שליחה מחדש מקישור יצירת המערכת." : "לא הצלחנו להתחבר כרגע. הקישור עדיין תקין — נסו שוב בעוד כמה דקות."}
          </div>
        ) : purchaseClaimToken ? (
          <div className="login-signup-status" role="status">
            {purchaseClaimState === "creating" || purchaseClaimState === "in_progress" || purchaseClaimState === "signing-in" ? <><span className="login-progress-spinner" aria-hidden="true" /><strong>{purchaseClaimState === "signing-in" ? "המערכת מוכנה, נכנסים…" : "המערכת נוצרת, נא להמתין…"}</strong><span>אין לסגור את החלון או לבצע רכישה נוספת.</span></> : null}
            {purchaseClaimState === "resent" ? <><strong>קישור הגישה נשלח מחדש</strong><span>בדקו את תיבת המייל שלכם.</span></> : null}
            {purchaseClaimState === "access-failed" ? <><strong>המערכת מוכנה, אך הכניסה האוטומטית לא הצליחה</strong><span>אפשר לנסות לפתוח שוב את קישור היצירה או לשלוח את קישור הגישה למייל.</span><button className="login-mode-toggle" type="button" disabled={isSubmitting} onClick={resendPurchaseAccess}>שליחה מחדש של קישור הגישה</button></> : null}
            {purchaseClaimState === "cooldown" ? <><strong>יש להמתין מעט</strong><span>שליחה מחדש זמינה פעם בדקה.</span></> : null}
            {purchaseClaimState === "expired" ? <><strong>הקישור פג</strong><span>פנו לתמיכה עם מספר ההזמנה.</span></> : null}
            {purchaseClaimState === "held_review" || purchaseClaimState === "revoked" ? <><strong>נדרשת בדיקה</strong><span>לא נוצרה מערכת נוספת. פנו לתמיכה עם מספר ההזמנה.</span></> : null}
            {purchaseClaimState === "email-failed" || purchaseClaimState === "error" ? <><strong>המערכת נשמרה, אך נדרשת עזרה בשליחת הגישה</strong><span>נסו שוב מאוחר יותר או פנו לתמיכה. אין לבצע רכישה נוספת.</span></> : null}
          </div>
        ) : STAGE5_DIRECT_FREE_ENABLED && signupMode ? (
          signupState === "check-email" ? (
            <div className="login-signup-status" role="status">
              <strong>שלחנו קישור למייל</strong>
              <span>פתחו את הקישור באותו מכשיר כדי להשלים את יצירת המערכת.</span>
            </div>
          ) : signupState === "creating" ? (
            <div className="login-signup-status login-signup-status--processing" role="status" aria-live="polite">
              <span className="login-progress-spinner" aria-hidden="true" />
              <strong>יוצרים את המערכת שלכם...</strong>
            </div>
          ) : signupState === "existing-event" ? (
            <div className="login-signup-status login-signup-status--warning" role="status">
              <span>כבר קיימת מערכת אישורי הגעה המשויכת למייל הזה. כדי להיכנס אליה, השתמשו בקישור שקיבלתם בעת פתיחת המערכת.</span>
              <button className="login-mode-toggle" type="button" onClick={() => setSignupState("idle")}>חזרה למסך ההרשמה</button>
            </div>
          ) : signupState === "unavailable" ? (
            <div className="login-signup-status login-signup-status--warning" role="status">
              <span>פתיחת מערכת חדשה עדיין אינה זמינה עבור כתובת זו.</span>
            </div>
          ) : signupState === "expired" || signupState === "used" ? (
            <div className="login-signup-status login-signup-status--warning" role="alert">
              <span>הקישור פג או כבר נוצל.</span>
              <button className="login-mode-toggle" type="button" onClick={() => setSignupState("idle")}>בקשת קישור חדש</button>
            </div>
          ) : isGoogleProcessing ? (
            <div className="login-signup-status login-signup-status--processing" role="status" aria-live="polite">
              <span className="login-progress-spinner" aria-hidden="true" />
              <strong>פותחים את המערכת...</strong>
            </div>
          ) : isEmailProcessing ? (
            <div className="login-signup-status login-signup-status--processing" role="status" aria-live="polite">
              <span className="login-progress-spinner" aria-hidden="true" />
              <strong>שולחים קישור למייל...</strong>
            </div>
          ) : (
            <>
              <div className="login-google-choice">
                <button className="login-google" type="button" onClick={handleGoogleSignup} disabled={isSubmitting}>
                  <svg className="login-google__icon" aria-hidden="true" viewBox="0 0 18 18">
                    <path fill="#4285F4" d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.797 2.715v2.258h2.909c1.702-1.567 2.684-3.874 2.684-6.613Z" />
                    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.182l-2.909-2.258c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.585-5.037-3.715H.956v2.332A9 9 0 0 0 9 18Z" />
                    <path fill="#FBBC05" d="M3.963 10.705A5.41 5.41 0 0 1 3.682 9c0-.592.102-1.168.281-1.705V4.963H.956A9 9 0 0 0 0 9c0 1.452.347 2.827.956 4.037l3.007-2.332Z" />
                    <path fill="#EA4335" d="M9 3.58c1.321 0 2.507.454 3.441 1.346l2.581-2.581C13.463.892 11.426 0 9 0A9 9 0 0 0 .956 4.963l3.007 2.332C4.672 5.165 6.656 3.58 9 3.58Z" />
                  </svg>
                  <span>הירשם עם Google</span>
                </button>
              </div>
              <div className="login-divider"><span>או המשיכו עם מייל</span></div>
              <form className="login-form login-form--signup" onSubmit={handleEmailSignup} noValidate>
                {SIGNUP_FIELDS.map(([key, label]) => (
                  <label className="login-field" key={key}>
                    <span className="login-field__label">{label}</span>
                    <span className="login-field__control">
                      <input
                        value={signup[key]}
                        onChange={(event) => {
                          const value = event.target.value;
                          setSignup((current) => ({ ...current, [key]: value }));
                          if (value.trim()) {
                            setSignupErrors((current) => {
                              if (!current[key]) return current;
                              const next = { ...current };
                              delete next[key];
                              return next;
                            });
                          }
                        }}
                        type={key === "email" ? "email" : "text"}
                        aria-invalid={Boolean(signupErrors[key])}
                        aria-describedby={signupErrors[key] ? `signup-${key}-error` : undefined}
                        required
                      />
                    </span>
                    {signupErrors[key] ? <span className="login-field__error" id={`signup-${key}-error`} role="alert">{signupErrors[key]}</span> : null}
                  </label>
                ))}
                <button className="login-form__submit" type="submit" disabled={isSubmitting}>{isSubmitting ? "שולחים..." : "שלחו לי קישור חד-פעמי"}</button>
              </form>
            </>
          )
        ) : (
        <form className="login-form" onSubmit={handleSubmit} noValidate>
          <label className="login-field">
            <span className="login-field__label">שם משתמש</span>
            <span className="login-field__control">
              <img src="/assets/icons/user.svg" alt="" aria-hidden="true" />
              <input
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                type="text"
                placeholder="הקלד כאן"
                autoComplete="username"
              />
            </span>
          </label>

          <label className="login-field">
            <span className="login-field__label">סיסמא</span>
            <span className="login-field__control">
              <img src="/assets/icons/pass.svg" alt="" aria-hidden="true" />
              <input
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                type="password"
                placeholder="הקלד כאן"
                autoComplete="current-password"
              />
            </span>
          </label>

          <button className="login-form__submit" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "מתחברים..." : "כניסה למערכת"}
          </button>
        </form>
        )}
        {eventAccessState === "idle" && !purchaseClaimToken && STAGE5_DIRECT_FREE_ENABLED && !initialSignupMode && signupState !== "existing-event" && (
          <button className="login-mode-toggle" type="button" onClick={() => { setSignupMode((value) => !value); setSignupState("idle"); }}>
            {signupMode ? "כבר יש לי מערכת — כניסה" : "אין לי מערכת — פתיחת מערכת חדשה"}
          </button>
        )}
      </section>
    </main>
  );
};

export default Login;
